import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalService } from '../../../core/services/modal.service';
import { InvoiceService } from '../../../core/services/invoice.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { Invoice, InvoiceLoadSnapshot } from '../../../core/models/invoice.model';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { CheckboxComponent } from '../../../shared/ui/forms/checkbox/checkbox.component';
import { FormFieldComponent } from '../../../shared/ui/forms/form-field/form-field.component';
import { InputComponent } from '../../../shared/ui/forms/input/input.component';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Component({
  selector: 'app-remove-load-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    TextComponent,
    LucideIconComponent,
    CheckboxComponent,
    FormFieldComponent,
    InputComponent,
  ],
  templateUrl: './remove-load-modal.component.html',
})
export class RemoveLoadModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly functions = inject(Functions);

  private readonly state = this.modalService.state();
  readonly invoice: Invoice = (this.state as any)?.data?.invoice;
  readonly loadSnapshot: InvoiceLoadSnapshot = (this.state as any)?.data?.loadSnapshot;
  readonly onSuccess = (this.state as any)?.data?.onSuccess;

  readonly returnToReady = signal(true);
  readonly reason = signal('');
  readonly recipientEmail = computed(() => this.invoice?.billedToEmail || this.invoice?.recipientEmail || '');
  readonly sendUpdatedEmail = signal(Boolean(this.invoice?.billedToEmail || this.invoice?.recipientEmail));
  readonly isRemoving = signal(false);

  close(): void {
    this.modalService.close();
  }

  async confirmRemove(): Promise<void> {
    if (!this.invoice || !this.loadSnapshot) return;

    this.isRemoving.set(true);
    const reasonText = this.reason().trim();
    const loadLabel = this.loadSnapshot.externalId || this.loadSnapshot.loadId.substring(0, 8);

    try {
      const { remainingLoadsCount, newTotal } = await this.invoiceService.removeLoadFromInvoice(
        this.invoice.id,
        this.loadSnapshot.loadId,
        this.returnToReady(),
        reasonText || `Load #${loadLabel} removed`
      );

      this.toastService.show(
        'success',
        `Load #${loadLabel} removed from Invoice #${this.invoice.invoiceNumber}. ${
          this.returnToReady() ? 'Returned to Ready to Invoice queue.' : 'Marked as Do Not Invoice.'
        }`
      );

      // If user requested to email the recipient with the update:
      const targetEmail = this.recipientEmail();
      if (this.sendUpdatedEmail() && targetEmail && remainingLoadsCount > 0) {
        try {
          const emailLoadDocs = httpsCallable(this.functions, 'emailLoadDocuments');
          const companyId = this.authService.activeCompanyId();
          const changeNotes = `Load #${loadLabel} was removed from this invoice. New invoice total is $${newTotal.toFixed(2)}.${
            reasonText ? ` Reason: ${reasonText}` : ''
          }`;

          await emailLoadDocs({
            invoiceNumber: this.invoice.invoiceNumber,
            companyId,
            recipientEmail: targetEmail,
            emailAction: 'UPDATE',
            changeNotes,
          });

          this.toastService.show('success', 'Updated invoice package emailed to recipient.');
        } catch (emailErr: any) {
          console.warn('Failed to send updated email', emailErr);
          this.toastService.show('warning', 'Load was removed, but failed to send update email.');
        }
      }

      this.modalService.close();
      this.onSuccess?.({ remainingLoadsCount, newTotal });
    } catch (err: any) {
      console.error('Failed to remove load from invoice:', err);
      this.toastService.show('error', err?.message || 'Failed to remove load from invoice.');
    } finally {
      this.isRemoving.set(false);
    }
  }
}
